import { RowClickedEvent } from '@ag-grid-enterprise/all-modules';
import { Box, makeStyles } from '@material-ui/core';
import { useParams } from '@reach/router';
import React, { useEffect, useState } from 'react';

import LoadingContainer from '../../../../../../components/LoadingContainer';
import TableGrid, { CellRenderer, MainColumns } from '../../../../../../components/TableGrid';
import TablePagination from '../../../../../../components/TableGrid/Pagination';
import { useTableGridApiState } from '../../../../../../components/TableGrid/TableGridApiContext/TableGridApiContext';
import { fetchCompanies } from '../../../../../../core/api/benchmark';
import { BenchmarkCompanyView } from '../../../../../../generated/model/benchmarkCompanyView';
import { useErrorsDispatch } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext';
import { ErrorContextActionTypes } from '../../../../../FinancialData/shared/ErrorContext/ErrorContext.types';
import { useBenchmarkDispatch, useBenchmarkState } from '../../../../Context/BenchmarkContext';
import { BenchmarkContextActionTypes } from '../../../../Context/BenchmarkContext.types';
import NavigationHeader from './NavigationHeader';

const SearchPanelCM: React.FC = () => {
  const classes = useStyles();
  const { pagination, refreshKey, companyFilter } = useBenchmarkState();
  const { tableGridApi } = useTableGridApiState();
  const dispatchBenchmark = useBenchmarkDispatch();
  const dispatchErrors = useErrorsDispatch();
  const { benchmarkId } = useParams();

  const [data, setData] = useState<BenchmarkCompanyView[]>([]);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(false);
  const pollingRef = React.useRef<NodeJS.Timeout | null>(null);

  const fetchCompaniesForPage = async () => {
    setLoading(true);
    try {
      // When filtering, fetch all matching companies at once (page 0, large size).
      // Without a filter, use normal pagination.
      const response = await fetchCompanies(benchmarkId, {
        page: companyFilter ? 0 : pagination.page > 0 ? pagination.page - 1 : 0,
        size: companyFilter ? pagination.totalElements || 10000 : pagination.size,
        ...(companyFilter ? { name: companyFilter } : {}),
      });
      const totalElements = response?.searchResult?.totalElements || 0;
      const size = pagination.size;
      const totalPages = Math.max(1, Math.ceil(totalElements / size));
      setData(response?.searchResult?.content || []);
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetPagination,
        payload: {
          ...pagination,
          totalElements,
          totalPages,
          // Reset to first page if current page is out of range
          page: pagination.page > totalPages ? 1 : pagination.page,
        },
      });
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetBenchmarkCompanies,
        payload: {
          companies: response.searchResult?.content || [],
          benchwiseState: response?.benchwiseState,
        },
      });
      // POLLING LOGIC: If benchwiseStage is FAST_REVIEW, poll again in 3 seconds
      const benchwiseStage = response?.benchwiseState?.benchwiseStage;
      if (benchwiseStage === 'FAST_REVIEW' || benchwiseStage === 'SLOW_REVIEW') {
        if (!polling) {
          setPolling(true);
        }
        if (pollingRef.current) clearTimeout(pollingRef.current);
        pollingRef.current = setTimeout(() => {
          fetchCompaniesForPage();
        }, 3000);
      } else {
        if (polling) {
          setPolling(false);
        }
        if (pollingRef.current) {
          clearTimeout(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch (error) {
      dispatchErrors({
        type: ErrorContextActionTypes.SetError,
        payload: { error },
      });
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompaniesForPage();
    return () => {
      if (pollingRef.current) {
        clearTimeout(pollingRef.current);
        pollingRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [benchmarkId, pagination.size, pagination.page, refreshKey, companyFilter]);

  const onRowClicked = (event: RowClickedEvent) => {
    if (event?.data?.companyInfo?.id) {
      dispatchBenchmark({
        type: BenchmarkContextActionTypes.SetActiveBenchmarkCompanyId,
        payload: event?.data?.companyInfo.id,
      });
    }
  };

  return (
    <Box className={classes.searchPanelBox}>
      <NavigationHeader />
      {loading ? (
        <LoadingContainer />
      ) : (
        <TableGrid
          translationKey="companies:companyInfo"
          columns={columnDefs}
          isShowSideBar={false}
          suppressTableContextProvider={true}
          originalProps={{
            headerHeight: 0,
            rowModelType: 'clientSide',
            rowData: data,
            pagination: false, // handled manually
            rowSelection: 'single',
            rowMultiSelectWithClick: false,
            defaultColDef: defaultColumnProp,
            onRowClicked: onRowClicked,
          }}
        >
          <TablePagination
            gridApi={tableGridApi}
            pagination={pagination}
            onChange={(value: number) => {
              dispatchBenchmark({
                type: BenchmarkContextActionTypes.SetPagination,
                payload: { ...pagination, page: value },
              });
            }}
          />
        </TableGrid>
      )}
    </Box>
  );
};

const defaultColumnProp = {
  resizable: true,
  flex: 1,
  minWidth: 100,
  menuTabs: [],
  sortable: false,
};

const columnDefs: MainColumns[] = [
  {
    headerName: '',
    field: 'companyInfo.name',
    minWidth: 300,
    cellClass: 'companyTitle',
    cellRenderer: CellRenderer.BenchmarkCompanyLink,
  },
];

const useStyles = makeStyles((theme) => ({
  searchPanelBox: {
    display: 'flex',
    flexDirection: 'column',
    width: '340px',
    paddingLeft: theme.spacing(4),
    '& .ag-row-selected': {
      backgroundColor: theme.palette.primary.main,
      '& .companyTitle': {
        color: theme.palette.background.paper,
      },
    },
    '& .ag-root-wrapper-body': {
      height: '100%',
    },
    '& .ag-theme-alpine .ag-header': {
      borderBottom: 'none',
    },
    marginRight: theme.spacing(8),
  },
}));
export default SearchPanelCM;
